import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackLead, trackCustomEvent } from '../utils/fbPixel';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, CreditCard, AlertCircle, FileX, Building, HelpCircle, MoreHorizontal, Home, Car, Banknote, TrendingUp, GraduationCap, ChevronLeft, CheckCircle2, Lock } from 'lucide-react';
const countryCodes = [{
  code: 'US',
  prefix: '+1'
}, {
  code: 'MX',
  prefix: '+52'
}, {
  code: 'CO',
  prefix: '+57'
}, {
  code: 'AR',
  prefix: '+54'
}, {
  code: 'ES',
  prefix: '+34'
}, {
  code: 'PR',
  prefix: '+1'
}, {
  code: 'DO',
  prefix: '+1'
}, {
  code: 'VE',
  prefix: '+58'
}, {
  code: 'PE',
  prefix: '+51'
}, {
  code: 'CL',
  prefix: '+56'
}];
export function HeroSection() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    situacion: '',
    meta: '',
    nombre: '',
    apellido: '',
    email: '',
    countryCode: '+1',
    telefono: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    // Enforce numeric only for phone
    if (e.target.name === 'telefono') {
      const val = e.target.value.replace(/[^0-9]/g, '');
      setFormData({
        ...formData,
        telefono: val
      });
      return;
    }
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const nextStep = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, 3));
  };
  const prevStep = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };
  const handleIconSelect = (field: 'situacion' | 'meta', value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
    trackCustomEvent('FormStepComplete', {
      step: field === 'situacion' ? 1 : 2,
      value
    });
    setTimeout(() => {
      nextStep();
    }, 300);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      nextStep();
    } else {
      // Track Lead event on Meta Pixel directly
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'OmniCredit Lead Form',
          situacion: formData.situacion,
          meta: formData.meta
        });
      }
      // Send data to webhook then navigate
      const params = new URLSearchParams();
      params.append('situacion', formData.situacion);
      params.append('meta', formData.meta);
      params.append('nombre', formData.nombre);
      params.append('apellido', formData.apellido);
      params.append('email', formData.email);
      params.append('phone', `${formData.countryCode}${formData.telefono}`);
      params.append('full_name', `${formData.nombre} ${formData.apellido}`);
      fetch('https://services.leadconnectorhq.com/hooks/FX4cPSwQF4w8Nu83Uo98/webhook-trigger/nZ0D76KQ1Dgtvs4v4Qbx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params.toString(),
        mode: 'no-cors'
      }).then(() => navigate('/thank-you')).catch(() => navigate('/thank-you'));
    }
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const renderDots = () => {
    const totalSteps = 3;
    return <div className="flex flex-col items-center mb-3 sm:mb-4" data-id="element-20">
        <span className="text-[10px] sm:text-xs font-bold text-slate-500 tracking-widest mb-2 uppercase" data-id="element-21">
          PASO {step} DE {totalSteps} · TOMA MENOS DE 60 SEGUNDOS
        </span>
        <div className="flex items-center gap-2" data-id="element-22">
          {[...Array(totalSteps)].map((_, i) => {
          const stepNum = i + 1;
          const isActive = stepNum === step;
          const isPast = stepNum < step;
          return <div key={i} className={`h-2 rounded-full transition-all duration-300 ${isActive ? 'w-8 bg-omni' : isPast ? 'w-2 bg-omni' : 'w-2 bg-slate-700'}`} data-id="element-23" />;
        })}
        </div>
      </div>;
  };
  return <section className="relative min-h-[100dvh] flex flex-col items-center pt-2 pb-8 md:pt-4 md:pb-24 overflow-hidden bg-dark-primary" data-id="element-24">
      {/* Background texture */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: 'radial-gradient(circle, #2DD4A8 0.8px, transparent 0.8px)',
      backgroundSize: '24px 24px'
    }} data-id="element-25"></div>

      {/* Background glow */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-26">
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-omni/5 blur-[120px]" data-id="element-27"></div>
      </div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative z-10" data-id="element-28">
        {/* Inline nav */}
        <div className="flex items-center justify-between mb-4 md:mb-8" data-id="element-29">
          <img src="https://cdn.magicpatterns.com/uploads/c2J7EfJRgjCnjErKDdmjhc/tmpu_weircg.webp" alt="OmniCredit Logo" className="h-8 md:h-12 object-contain" data-id="element-30" />
          <a href="tel:+18001234567" className="flex items-center gap-2 text-omni hover:text-omni-light font-bold text-sm sm:text-base tracking-wide transition-colors" data-id="element-31">
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-32" />
            <span data-id="element-33">LLAMA AHORA</span>
          </a>
        </div>

        <div className="max-w-4xl mx-auto text-center flex flex-col items-center mb-5 md:mb-12" data-id="element-34">
          <h1 className="text-2xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white mb-1 md:mb-4 leading-[1.1]" data-id="element-35">
            Mejora Tu Crédito Hoy
          </h1>

          <h2 className="text-lg sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-omni to-omni-light mb-1 md:mb-6" data-id="element-36">
            ¿Cuánto Podrías Mejorar Tu Puntuación?
          </h2>

          <p className="text-sm md:text-xl text-slate-300" data-id="element-37">
            Comience su evaluación GRATUITA hoy.
          </p>
        </div>

        {/* Multi-step Form Card */}
        <div className="max-w-3xl mx-auto bg-dark-secondary border border-slate-700/50 rounded-2xl shadow-2xl p-4 sm:p-10 relative overflow-hidden" data-id="element-38">
          {renderDots()}

          <div className="min-h-[240px] md:min-h-[300px] relative" data-id="element-39">
            <AnimatePresence custom={direction} mode="wait" data-id="element-40">
              {/* STEP 1: Situación */}
              {step === 1 && <motion.div key="step1" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="flex flex-col h-full" data-id="element-41">
                  <h3 className="text-lg sm:text-2xl font-medium text-white text-center mb-1" data-id="element-42">
                    ¿Qué situación describe mejor su caso?
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 text-center mb-3 sm:mb-6" data-id="element-43">
                    Elija una opción para recibir su evaluación gratuita
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-44">
                    {[{
                  id: 'deudas',
                  label: 'Deudas',
                  desc: 'Tarjetas, préstamos',
                  icon: CreditCard
                }, {
                  id: 'cobranzas',
                  label: 'Cobranzas',
                  desc: 'Agencias llamándole',
                  icon: AlertCircle
                }, {
                  id: 'reportes',
                  label: 'Reportes negativos',
                  desc: 'Crédito dañado',
                  icon: FileX
                }, {
                  id: 'bancarrota',
                  label: 'Bancarrota',
                  desc: 'Considerando opciones',
                  icon: Building
                }, {
                  id: 'noseguro',
                  label: 'No estoy seguro',
                  desc: 'Necesito orientación',
                  icon: HelpCircle
                }, {
                  id: 'otro',
                  label: 'Otro',
                  desc: 'Otra situación',
                  icon: MoreHorizontal
                }].map(item => {
                  const Icon = item.icon;
                  const isSelected = formData.situacion === item.id;
                  return <button key={item.id} onClick={() => handleIconSelect('situacion', item.id)} className={`flex flex-col items-center justify-center py-3 px-2 sm:py-5 sm:px-3 rounded-xl border transition-all duration-200 ${isSelected ? 'border-omni bg-omni/10' : 'border-slate-700 bg-dark-tertiary hover:bg-slate-800 hover:border-omni/50 hover:shadow-[0_0_12px_rgba(45,212,168,0.15)] hover:-translate-y-0.5'}`} data-id="element-45">
                          <Icon strokeWidth={1.8} className="w-7 h-7 sm:w-9 sm:h-9 mb-1.5 sm:mb-2 text-omni" data-id="element-46" />
                          <span className="text-[13px] sm:text-[15px] font-medium text-white text-center mb-0.5" data-id="element-47">
                            {item.label}
                          </span>
                          <span className="text-[10px] sm:text-xs text-slate-500 text-center" data-id="element-48">
                            {item.desc}
                          </span>
                        </button>;
                })}
                  </div>

                  <div className="mt-3 sm:mt-5" data-id="element-49">
                    <div className="h-px bg-slate-700/50 w-full mb-2 sm:mb-4" data-id="element-50"></div>
                    <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-slate-500" data-id="element-51">
                      <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-52" />
                      <span data-id="element-53">
                        100% confidencial · Sin compromiso · Evaluación gratis
                      </span>
                    </div>
                  </div>
                </motion.div>}

              {/* STEP 2: Meta */}
              {step === 2 && <motion.div key="step2" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} className="flex flex-col h-full" data-id="element-54">
                  <h3 className="text-lg sm:text-2xl font-medium text-white text-center mb-1" data-id="element-55">
                    ¿Cuál es su meta principal?
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-400 text-center mb-3 sm:mb-6" data-id="element-56">
                    Esto nos ayuda a personalizar su evaluación
                  </p>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-57">
                    {[{
                  id: 'casa',
                  label: 'Comprar Casa',
                  desc: 'Primera vivienda',
                  icon: Home
                }, {
                  id: 'auto',
                  label: 'Comprar Auto',
                  desc: 'Financiamiento vehicular',
                  icon: Car
                }, {
                  id: 'prestamo',
                  label: 'Obtener Préstamo',
                  desc: 'Préstamo personal',
                  icon: Banknote
                }, {
                  id: 'puntaje',
                  label: 'Mejorar Puntaje',
                  desc: 'Subir mi score',
                  icon: TrendingUp
                }, {
                  id: 'estudiantil',
                  label: 'Préstamo Estudiantil',
                  desc: 'Deuda educativa',
                  icon: GraduationCap
                }, {
                  id: 'otro',
                  label: 'Otro',
                  desc: 'Otra meta',
                  icon: MoreHorizontal
                }].map(item => {
                  const Icon = item.icon;
                  const isSelected = formData.meta === item.id;
                  return <button key={item.id} onClick={() => handleIconSelect('meta', item.id)} className={`flex flex-col items-center justify-center py-3 px-2 sm:py-5 sm:px-3 rounded-xl border transition-all duration-200 ${isSelected ? 'border-omni bg-omni/10' : 'border-slate-700 bg-dark-tertiary hover:bg-slate-800 hover:border-omni/50 hover:shadow-[0_0_12px_rgba(45,212,168,0.15)] hover:-translate-y-0.5'}`} data-id="element-58">
                          <Icon strokeWidth={1.8} className="w-7 h-7 sm:w-9 sm:h-9 mb-1.5 sm:mb-2 text-omni" data-id="element-59" />
                          <span className="text-[13px] sm:text-[15px] font-medium text-white text-center mb-0.5" data-id="element-60">
                            {item.label}
                          </span>
                          <span className="text-[10px] sm:text-xs text-slate-500 text-center" data-id="element-61">
                            {item.desc}
                          </span>
                        </button>;
                })}
                  </div>

                  <div className="mt-3 sm:mt-5" data-id="element-62">
                    <div className="h-px bg-slate-700/50 w-full mb-2 sm:mb-4" data-id="element-63"></div>
                    <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-xs text-slate-500" data-id="element-64">
                      <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-65" />
                      <span data-id="element-66">
                        100% confidencial · Sin compromiso · Evaluación gratis
                      </span>
                    </div>
                  </div>
                </motion.div>}

              {/* STEP 3: Información Personal */}
              {step === 3 && <motion.form key="step3" custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.3
            }} onSubmit={handleSubmit} className="flex flex-col h-full justify-center max-w-xl mx-auto w-full" data-id="element-67">
                  <h3 className="text-[22px] sm:text-2xl font-medium text-white text-center mb-6" data-id="element-68">
                    Información personal
                  </h3>

                  <div className="space-y-5 mb-8" data-id="element-69">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-70">
                      <div className="space-y-2" data-id="element-71">
                        <label htmlFor="nombre" className="text-sm font-medium text-slate-300" data-id="element-72">
                          Nombre
                        </label>
                        <input type="text" id="nombre" name="nombre" required value={formData.nombre} onChange={handleChange} className="w-full px-4 py-3.5 rounded-lg bg-dark-tertiary border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-omni focus:border-omni outline-none transition-all" placeholder="Juan" data-id="element-73" />
                      </div>
                      <div className="space-y-2" data-id="element-74">
                        <label htmlFor="apellido" className="text-sm font-medium text-slate-300" data-id="element-75">
                          Apellido
                        </label>
                        <input type="text" id="apellido" name="apellido" required value={formData.apellido} onChange={handleChange} className="w-full px-4 py-3.5 rounded-lg bg-dark-tertiary border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-omni focus:border-omni outline-none transition-all" placeholder="Pérez" data-id="element-76" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-77">
                      <label htmlFor="email" className="text-sm font-medium text-slate-300" data-id="element-78">
                        Email
                      </label>
                      <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3.5 rounded-lg bg-dark-tertiary border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-omni focus:border-omni outline-none transition-all" placeholder="juan@ejemplo.com" data-id="element-79" />
                    </div>

                    <div className="space-y-2" data-id="element-80">
                      <label htmlFor="telefono" className="text-sm font-medium text-slate-300" data-id="element-81">
                        Teléfono
                      </label>
                      <div className="flex gap-3" data-id="element-82">
                        <select name="countryCode" value={formData.countryCode} onChange={handleChange} className="w-28 px-3 py-3.5 rounded-lg bg-dark-tertiary border border-slate-600 text-white focus:ring-2 focus:ring-omni focus:border-omni outline-none transition-all appearance-none" data-id="element-83">
                          {countryCodes.map(c => <option key={c.code} value={c.prefix} data-id="element-84">
                              {c.code} {c.prefix}
                            </option>)}
                        </select>
                        <input type="tel" inputMode="numeric" pattern="[0-9]*" id="telefono" name="telefono" required value={formData.telefono} onChange={handleChange} className="flex-1 px-4 py-3.5 rounded-lg bg-dark-tertiary border border-slate-600 text-white placeholder-slate-500 focus:ring-2 focus:ring-omni focus:border-omni outline-none transition-all" placeholder="Número de teléfono" data-id="element-85" />
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="w-full bg-omni hover:bg-omni-dark text-dark-primary font-bold py-4 px-8 rounded-lg transition-colors text-lg" data-id="element-86">
                    Enviar Solicitud
                  </button>
                </motion.form>}
            </AnimatePresence>
          </div>

          {/* Back Button */}
          {step > 1 && step < 4 && <div className="absolute top-6 left-6 sm:top-10 sm:left-10" data-id="element-87">
              <button onClick={prevStep} className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm font-medium" data-id="element-88">
                <ChevronLeft className="w-4 h-4" data-id="element-89" />
                <span className="hidden sm:inline" data-id="element-90">Anterior</span>
              </button>
            </div>}
        </div>

        {/* Secondary CTA — Phone */}
        <div className="max-w-3xl mx-auto mt-10 md:mt-12 text-center" data-id="element-91">
          <p className="text-sm text-slate-400 mb-4" data-id="element-92">
            ¿Prefiere hablar directamente?
          </p>
          <a href="tel:+18001234567" className="inline-flex items-center gap-3 bg-transparent border border-omni/40 hover:border-omni text-omni hover:text-omni-light font-bold py-3 px-8 rounded-xl transition-all hover:shadow-[0_0_20px_rgba(45,212,168,0.2)]" data-id="element-93">
            <Phone className="w-5 h-5" data-id="element-94" />
            <span data-id="element-95">LLAMA AHORA</span>
          </a>
        </div>
      </div>
    </section>;
}